import { useEffect } from 'react';
import { Sheets } from './components/Sheets';
import { StatusBar } from './components/StatusBar';
import { DayScreen } from './screens/DayScreen';
import { DoneScreen } from './screens/DoneScreen';
import { FailConfirmScreen } from './screens/FailConfirmScreen';
import { FailScreen } from './screens/FailScreen';
import { LoadScreen } from './screens/LoadScreen';
import { LoginScreen } from './screens/LoginScreen';
import { ManualScreen } from './screens/ManualScreen';
import { QueueScreen } from './screens/QueueScreen';
import { ReturnScreen } from './screens/ReturnScreen';
import { RideScreen } from './screens/RideScreen';
import { ScanResultScreen } from './screens/ScanResultScreen';
import { ScannerScreen } from './screens/ScannerScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { StopScreen } from './screens/StopScreen';
import { captureInstallPrompt, isIosBrowser } from './lib/install';
import { allHandled } from './state/route';
import { selectQueueCount, useAppStore } from './state/store';

const QUEUE_TICK_MS = 10_000;

function CurrentScreen() {
  const screen = useAppStore((s) => s.screen);
  switch (screen) {
    case 'login':
      return <LoginScreen />;
    case 'day':
      return <DayScreen />;
    case 'load':
      return <LoadScreen />;
    case 'ride':
      return <RideScreen />;
    case 'stop':
      return <StopScreen />;
    case 'scanner':
      return <ScannerScreen />;
    case 'scanresult':
      return <ScanResultScreen />;
    case 'manual':
      return <ManualScreen />;
    case 'fail':
      return <FailScreen />;
    case 'failconfirm':
      return <FailConfirmScreen />;
    case 'return':
      return <ReturnScreen />;
    case 'done':
      return <DoneScreen />;
    case 'queue':
      return <QueueScreen />;
    case 'settings':
      return <SettingsScreen />;
    default:
      return <DayScreen />;
  }
}

export function App() {
  const ready = useAppStore((s) => s.ready);
  const online = useAppStore((s) => s.online);
  const screen = useAppStore((s) => s.screen);
  const session = useAppStore((s) => s.session);
  const route = useAppStore((s) => s.route);
  const prefs = useAppStore((s) => s.prefs);
  const queueCount = useAppStore(selectQueueCount);
  const init = useAppStore((s) => s.init);
  const setOnline = useAppStore((s) => s.setOnline);
  const navigate = useAppStore((s) => s.navigate);
  const openSheet = useAppStore((s) => s.openSheet);
  const setInstallPromptAvailable = useAppStore((s) => s.setInstallPromptAvailable);

  useEffect(() => {
    void init();
  }, [init]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [setOnline]);

  // Achtergrondsync: de wachtrij loopt vanzelf leeg, de bezorger hoeft niets.
  useEffect(() => {
    const timer = window.setInterval(() => {
      void useAppStore.getState().flushQueue();
    }, QUEUE_TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void useAppStore.getState().flushQueue();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Browser-back gedraagt zich als de terugknop en verlaat de app nooit ongemerkt.
  useEffect(() => {
    window.history.pushState({ gd: true }, '');
    const onPop = () => {
      useAppStore.getState().back();
      window.history.pushState({ gd: true }, '');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Android: het installatievoorstel bewaren tot de bezorger het zelf wil.
  useEffect(() => {
    const onPrompt = (event: Event) => {
      captureInstallPrompt(event);
      setInstallPromptAvailable(true);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, [setInstallPromptAvailable]);

  // iOS Safari: eenmalig uitleggen hoe de app op het beginscherm komt (B3).
  useEffect(() => {
    if (!ready || !session || prefs.iosInstallHintSeen) return;
    if (screen !== 'day' || !isIosBrowser()) return;
    openSheet('install');
  }, [ready, session, prefs.iosInstallHintSeen, screen, openSheet]);

  // Zodra geen stop meer openstaat verschijnt de terugrit vanzelf (I1).
  useEffect(() => {
    if (!route) return;
    if (screen === 'ride' && allHandled(route) && route.route.status !== 'completed') {
      navigate('return');
    }
  }, [route, screen, navigate]);

  if (!ready) return <div className="app" />;

  return (
    <div className="app">
      <StatusBar online={online} queueCount={queueCount} />
      <CurrentScreen />
      <Sheets />
    </div>
  );
}
